import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/i18n";
import { Flame, Zap, Target, Flag, Repeat } from "lucide-react";

const DAYS = ["L", "M", "M", "J", "V", "S", "D"];
const DAYS_EN = ["M", "T", "W", "T", "F", "S", "S"];

export const StreakCard = ({ streak }) => {
  const { t, lang } = useI18n();
  if (!streak) return null;
  const dias = lang === "en" ? DAYS_EN : DAYS;
  const uno = streak.current === 1;
  return (
    <Card className="bg-slate-900 border-slate-900 text-white p-5" data-testid="practice-streak">
      <div className="flex items-center gap-4">
        <span className="h-12 w-12 rounded-xl bg-orange-500/20 grid place-items-center shrink-0">
          <Flame className={`h-6 w-6 ${streak.current ? "text-orange-400" : "text-slate-500"}`} />
        </span>
        <div className="min-w-0">
          <p className="text-2xl font-bold leading-none" data-testid="streak-count">
            {streak.current} {t(uno ? "cj_streak_day_one" : "cj_streak_days")}
          </p>
          <p className="text-xs text-slate-400 mt-1 truncate">
            {t(uno ? "cj_streak_row_one" : "cj_streak_row")}
            {streak.best ? ` · ${t("cj_streak_best")}: ${streak.best}` : ""}
          </p>
        </div>
      </div>
      <div className="flex gap-1.5 mt-4">
        {(streak.week || []).map((d, i) => (
          <span key={d.date} data-testid={`streak-day-${i}`}
                className={`flex-1 h-7 rounded-md grid place-items-center text-[11px] font-bold ${
                  d.done ? "bg-orange-500 text-white"
                    : d.today ? "bg-white/10 text-slate-300 ring-1 ring-orange-400/60"
                      : "bg-white/10 text-slate-500"}`}>
            {dias[i]}
          </span>
        ))}
      </div>
    </Card>
  );
};

export const VerbOfDayCard = ({ vod, onStart, busy }) => {
  const { t, lang } = useI18n();
  if (!vod) return null;
  return (
    <Card className="border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 p-5
                     flex flex-wrap items-center gap-4" data-testid="verb-of-day">
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-orange-700">
          {t("cj_vod_title")}
        </p>
        <p className="text-2xl font-bold tracking-tight mt-1" data-testid="vod-verb">{vod.verb}</p>
        <p className="text-xs text-slate-500 mt-0.5">
          {lang === "en" ? (vod.tense_label_en || vod.tense_label) : vod.tense_label}
          {vod.translation ? ` · ${vod.translation}` : ""} · {t("cj_vod_note", { n: vod.count })}
        </p>
      </div>
      <Button onClick={onStart} disabled={busy} data-testid="vod-start-btn"
              className="bg-slate-900 hover:bg-slate-800">
        <Zap className="h-4 w-4" /> {t("cj_vod_go")}
      </Button>
    </Card>
  );
};

export const WeakVerbsCard = ({ verbs, onStart, busy }) => {
  const { t } = useI18n();
  if (!verbs?.length) return null;
  const peor = Math.max(...verbs.map((v) => v.misses));
  return (
    <Card className="p-5" data-testid="weak-verbs">
      <h3 className="text-base font-semibold flex items-center gap-2">
        <Target className="h-4 w-4 text-rose-600" /> {t("cj_weak_title")}
      </h3>
      <div className="mt-3 space-y-0.5">
        {verbs.map((v) => (
          <div key={v.verb} className="flex items-center gap-3 text-sm py-2 border-b border-slate-100 last:border-0"
               data-testid={`weak-verb-${v.verb}`}>
            <span className="font-semibold w-24 truncate">{v.verb}</span>
            <span className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <span className="block h-full rounded-full bg-orange-400"
                    style={{ width: `${Math.round((v.misses / peor) * 100)}%` }} />
            </span>
            <span className="text-xs font-semibold text-rose-600 whitespace-nowrap">
              {t(v.misses === 1 ? "cj_weak_misses_one" : "cj_weak_misses", { n: v.misses })}
            </span>
          </div>
        ))}
      </div>
      <Button variant="outline" className="mt-4" onClick={onStart} disabled={busy}
              data-testid="weak-start-btn">
        <Repeat className="h-4 w-4" /> {t("cj_weak_practice")}
      </Button>
    </Card>
  );
};

export const ChallengeCard = ({ challenge, onStart, busy }) => {
  const { t, lang } = useI18n();
  if (!challenge) return null;
  const pct = Math.round((challenge.done / challenge.target) * 100);
  const tense = lang === "en" ? (challenge.tense_label_en || challenge.tense_label) : challenge.tense_label;
  return (
    <Card className="p-5" data-testid="weekly-challenge">
      <h3 className="text-base font-semibold flex items-center gap-2">
        <Flag className="h-4 w-4 text-blue-600" /> {t("cj_challenge_title")}
      </h3>
      <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
        {t("cj_challenge_text")} <strong className="text-slate-900">{tense}</strong>.
      </p>
      <span className="block mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <span className="block h-full rounded-full bg-blue-500 transition-all duration-500"
              style={{ width: `${pct}%` }} />
      </span>
      <div className="flex items-center justify-between mt-2">
        <p className="text-xs text-slate-500" data-testid="challenge-progress">
          {t("cj_challenge_progress", { done: challenge.done, total: challenge.target })}
        </p>
        <button onClick={onStart} disabled={busy} data-testid="challenge-start-btn"
                className="text-xs font-semibold text-blue-700 hover:text-blue-900 disabled:opacity-50">
          {t("cj_challenge_go")}
        </button>
      </div>
    </Card>
  );
};
